import { useEffect, useState } from "react";
import { Box, Interpolation, TagColor, ZIndex } from "@gocardless/flux-react";
import { keyframes } from "@emotion/react";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import {
  PaymentType,
  TransactionBoxProps,
} from "./IBPPromoTransactionAnimation";
import {
  PaymentTypeTagConfigs,
  TRANSACTION_BOX_DEFAULT_SCALE,
  TransactionBox,
} from "./TransactionBox";

const DD_ANIMATION_DURATION = 5.6;
const COUNTER_DELAY = 50;
const ANIMATION_DELAY = 3.6 * 1000;
const MAX_AMOUNT = 45;

export const DirectDebitAnimation: React.FC<TransactionBoxProps> = ({
  animationRunningOn,
  restartAnimation,
  setAnimationRunningOn,
  setRestartAnimation,
}: TransactionBoxProps) => {
  const [animate, setAnimate] = useState(false);
  const { i18n } = useLingui();

  const titleString = i18n._(
    t({ id: "ibp.promo.dd_title", message: "Due monthly on the 23rd" })
  );
  const paymentScheduleString = i18n._(
    t({ id: "ibp.promo.dd_payment_schedule", message: "Recurring payment" })
  );

  const paymentTypeString = i18n._(
    t({ id: "ibp.promo.dd_payment_type", message: "Direct Debit" })
  );

  const zoomInZoomOut = keyframes`
    0% {
      transform: scale(${TRANSACTION_BOX_DEFAULT_SCALE}, ${TRANSACTION_BOX_DEFAULT_SCALE});
    }

    10%,90% {
      transform: scale(0.9, 0.9);
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.30));
    }

    100% {
      transform: scale(${TRANSACTION_BOX_DEFAULT_SCALE}, ${TRANSACTION_BOX_DEFAULT_SCALE});
    }
`;

  const zoomInZoomOutAnimation: Interpolation = {
    animation: `${zoomInZoomOut} ${DD_ANIMATION_DURATION}s ease-in-out`,
    transition: "filter 100ms ease-in-out",
  };

  const paymentTypeTagConfigs: PaymentTypeTagConfigs = {
    baseColor: TagColor.Neutral,
    transitionColor: TagColor.Neutral,
  };

  useEffect(() => {
    setAnimate(true);
  }, []);

  return (
    <Box
      style={{
        marginTop: "-30px",
        position: "relative",
        zIndex:
          animationRunningOn === PaymentType.DIRECT_DEBIT ? ZIndex.Dialog : 1,
      }}
    >
      <TransactionBox
        title={titleString}
        maxAmount={MAX_AMOUNT}
        paymentSchedule={paymentScheduleString}
        paymentType={paymentTypeString}
        restartAnimation={restartAnimation}
        onAnimationFinished={() => {
          setTimeout(() => {
            setRestartAnimation(true);
            setTimeout(() => {
              setAnimationRunningOn(PaymentType.IBP);
            }, 1000);
          }, ANIMATION_DELAY);
        }}
        isAnimationApplied={animationRunningOn === PaymentType.DIRECT_DEBIT}
        counterDelay={COUNTER_DELAY}
        paymentTypeTagConfig={paymentTypeTagConfigs}
        animation={animate ? zoomInZoomOutAnimation : {}}
      />
    </Box>
  );
};
